<template>
    <div class="exception-settings">
        <div class="td-banner info my-2">
            <icon name="faCircleInfo" type="solid"></icon>
            <p>Exceptions allow you to create a set of hours of operation rules for a specifc day that will override the regularly scheduled hours of operation on that day.</p>
        </div>
        <div v-if="addException" class="my-2 td-card grey border rounded">
            <div class="td-flex">
                <h5 class="td-h5">New Hours Exception</h5>
                <label class="td-checkbox-label mb-1" for="enabled">
                    Enable
                    <input name="enabled" id="enabled" type="checkbox" v-model="exceptionModel.enabled" />
                    <span></span>
                </label>
            </div>

            <div class="col-2-form">
                <label class="td-label required" for="exception-name">Name:</label>
                <div>
                    <input type="text" id="exception-name" v-model="exceptionModel.name"
                           @input="exceptionValidators['new'].validate('name-new')" placeholder="Exception Name"
                           :class="[{'error': exceptionValidators['new'].errors['name-new']}, 'td-input']" />
                    <error v-if="exceptionValidators['new'].errors['name-new']" :error="exceptionValidators['new'].errors['name-new']"></error>
                </div>

            </div>

            <div class="col-1 lg-col-2">
                <div class="col-2-form">
                    <label for="e-date" class="td-label required">Date:</label>
                    <div>
                        <VueDatePicker id="e-date" 
                            v-model="exceptionModel.dateRange" 
                            placeholder="Exception date(s)" 
                            @update:model-value="exceptionValidators['new'].validate('date-new')"
                            :formats="{ 'preview': 'yyyy-MM-dd' }"
                            :month-change-on-scroll="false" 
                            teleport-center :range="true" />
                        <error v-if="exceptionValidators['new'].errors['date-new']" :error="exceptionValidators['new'].errors['date-new']"></error>
                    </div>

                </div>
                <div class="col-2-form has-btn">
                    <label class="td-label" for="e-operation-hours">Hours:</label>
                    <div>
                        <select v-model="exceptionModel.type" class="td-select" id="e-operation-hours" @change="changeNewExceptionHoursType">
                            <option :value="HoursOfOperationType.AlwaysOpen">Always Open</option>
                            <option :value="HoursOfOperationType.SelectHours">Select Hours</option>
                            <option :value="HoursOfOperationType.Closed">Closed</option>
                        </select>
                        <error v-if="exceptionValidators['new'].errors['type-new']" :error="exceptionValidators['new'].errors['type-new']"></error>
                    </div>
                    <button @click.prevent="addNewExceptionEntry" v-if="exceptionModel.type == HoursOfOperationType.SelectHours"
                            class="secondary-outlined icon-left td-btn">
                        <icon type="solid" name="faPlus"></icon> Add Time
                    </button>

                </div>

            </div>
           
            <div v-for="(entry, entryIndex) in exceptionModel.entries.entries" v-if="exceptionModel.type == HoursOfOperationType.SelectHours">
                <div class="add-hours-row td-card border rounded">
                    <div class="td-radio background ml-0">
                        <label :for="'open-time-new' + entryIndex">
                            <input type="radio"
                                   v-model="entry.type"
                                   :id="'open-time-new' + entryIndex"
                                   :name="'open-time-new' + entryIndex"
                                   :value="HoursOfOperationDisplayType.Open" />
                            <span>Open </span>
                        </label>

                        <label :for="'after-hours-new' + entryIndex">
                            <input type="radio"
                                   v-model="entry.type"
                                   :id="'after-hours-new' + entryIndex"
                                   :name="'after-hours-new' + entryIndex"
                                   :value="HoursOfOperationDisplayType.AfterHours" />
                            <span>After Hours </span>
                        </label>
                    </div>
                    <div class="hours-entry">
                        <input type="time" v-model="entry.startTime" @change="editTime('new', entryIndex)"
                               :class="[{ error: hasStartTimeError('new', entryIndex) }, 'td-input']" />
                        -
                        <input type="time" v-model="entry.endTime" @change="editTime('new', entryIndex)"
                               :class="[{ error: hasEndTimeError('new', entryIndex) }, 'td-input']" />
                    </div>
                    <div class="icons-actions pr-1 td-text-right">
                        <button @click.prevent="deleteNewEntry(entryIndex)" class="td-btn danger icon-only">
                            <icon type="solid" name="faTrash"></icon>
                        </button>
                    </div>
                </div>
                <div>
                    <error v-if="exceptionValidators[`new-${entryIndex}`]?.errors[`startTime-new-${entryIndex}`]"
                           :error="exceptionValidators[`new-${entryIndex}`].errors[`startTime-new-${entryIndex}`]"></error>
                    <error v-if="exceptionValidators[`new-${entryIndex}`]?.errors[`endTime-new-${entryIndex}`]"
                           :error="exceptionValidators[`new-${entryIndex}`].errors[`endTime-new-${entryIndex}`]"></error>
                    <error v-if="exceptionValidators[`new-${entryIndex}`]?.errors[`overlap-new-${entryIndex}`]"
                           :error="exceptionValidators[`new-${entryIndex}`].errors[`overlap-new-${entryIndex}`]"></error>
                    <error v-if="exceptionValidators[`new-${entryIndex}`]?.errors[`range-new-${entryIndex}`]"
                           :error="exceptionValidators[`new-${entryIndex}`].errors[`range-new-${entryIndex}`]"></error>
                </div>
            </div>
           
            <error v-if="exceptionValidators['new'].errors['entries-new']" :error="exceptionValidators['new'].errors['entries-new']"></error>
           
            <div class="td-flex mt-2">
                <button class="td-btn danger" @click.prevent="cancelExceptionAdd">Cancel</button>
                <button class="td-btn secondary" @click.prevent="confirmExceptionAdd">Add Exception</button>
            </div>

        </div>
        <div v-if="tempExceptions.length > 0" class="col-1 exception-settings">
            <div class="td-table-container">
                <table cellspacing="0" cellpadding="0" class="td-table">
                    <thead>
                        <tr class="table-header hours-row">
                            <th class="pl-1 name">Name</th>
                            <th>Status</th>
                            <th class="td-date">Date</th>
                            <th>Type</th>
                            <th class="pr-1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(exception, index) in tempExceptions" :key="index">
                            <tr class="table-row hours-row" v-if="!isEditing[index]">
                                <td class="pl-1 name">
                                    <label class="td-checkbox-label mb-1 td-text-secondary" :for="`exception-${index}`">
                                         <strong>{{ exception.name }}</strong>
                                        <input :name="`exception-${index}`" :id="`exception-${index}`" type="checkbox" v-model="exception.enabled" @change="updateEnabled(index)" />
                                        <span></span>
                                    </label>

                                </td>
                                <td>
                                    <span class="td-badge success no-icon ml-1" v-if="exception.enabled">Enabled</span>
                                    <span class="td-badge td-text-danger error no-icon ml-1" v-if="!exception.enabled">Disabled</span>
                                </td>

                                <td class="td-date">
                                    <strong :class="['show-mobile', { 'td-text-subdued': !exception.enabled }]">Date</strong>
                                    {{dateRangeDisplay(exception.dateRange)}}
                                </td>
                                <td>
                                    <strong :class="['show-mobile',{ 'td-text-subdued': !exception.enabled }]">Type</strong>
                                    {{displayHoursType(exception.type)}}
                                    <div v-if="exception.type === HoursOfOperationType.SelectHours" class="mt-1 td-text-italic td-text-subdued">
                                        <strong :class="['show-mobile', 'mr-2',{ 'td-text-subdued': !exception.enabled }]">Hours</strong>
                                        <span v-for="entry in exception.entries.entries">{{entry.startTime}} - {{entry.endTime}} ({{displayEntryHoursType(entry.type)}})<br /></span>
                                    </div>
                                </td>
                                <td class="show-mobile"></td>
                                <td class="pr-1 td-text-right table-actions">
                                    <button @click.prevent="toggleEditException(index)" class="secondary td-btn icon-only ma-1"><icon name="faPen" type="solid"></icon></button>
                                    <button @click.prevent="deleteException(index)" class="danger td-btn icon-only ma-1"><icon name="faTrash" type="solid"></icon></button>
                                </td>

                            </tr>
                            <!-- EDIT EXCEPTION -->
                            <tr v-if="isEditing[index]" class="hours-row edit-record table-row">
                                <td class="pl-1 name">
                                    <strong :class="['show-mobile', { 'td-text-subdued': !exception.enabled }]">Name</strong>
                                    <div class="td-flex">
                                        <label class="td-checkbox-label mb-4 td-text-bold" :for="`exception-${index}`">

                                            <input :name="`exception-${index}`" :id="`exception-${index}`" type="checkbox" v-model="exception.enabled" />
                                            <span></span>
                                        </label>
                                        <input :class="[{'error': exceptionValidators[index].errors[`name-${index}`]}, 'td-input', 'name-input']" v-model="exception.name"
                                               type="text" placeholder="Exception Name" @input="exceptionValidators[index].validate(`name-${index}`)" />
                                    </div>

                                </td>
                                <td>
                                    <span class="td-badge success no-icon slim ml-1" v-if="exception.enabled">Enabled</span>
                                    <span class="td-badge td-text-danger error no-icon slim ml-1" v-if="!exception.enabled">Disabled</span>
                                </td>

                                <td class="td-date">
                                    <strong :class="['show-mobile', { 'td-text-subdued': !exception.enabled }]">Date</strong>
                                    <VueDatePicker id="e-date" 
                                        v-model="exception.dateRange" 
                                        placeholder="Exception date(s)"
                                        range
                                        :formats="{ 'preview': 'yyyy-MM-dd', 'input': 'yyyy-MM-dd' }" :time-config="{ enableTimePicker: false }"
                                        :state="exceptionValidators[index].errors[`date-${index}`]"
                                        @change="exceptionValidators[index].validate(`date-${index}`)" />

                                </td>
                                <td>
                                    <strong :class="['show-mobile',{ 'td-text-subdued': !exception.enabled }]">Type</strong>
                                    <select v-model="exception.type" :class="[{'error': exceptionValidators[index].errors[`type-${index}`]}, 'td-select']" id="e-operation-hours"
                                            @change="changeExceptionHoursTypeEdit(index)">
                                        <option :value="HoursOfOperationType.AlwaysOpen">Always Open</option>
                                        <option :value="HoursOfOperationType.SelectHours">Select Hours</option>
                                        <option :value="HoursOfOperationType.Closed">Closed</option>
                                    </select>

                                </td>
                                <td class="show-mobile"></td>
                                <td class="pr-1 td-text-right table-actions">
                                    <button @click.prevent="addEmptyEntry(index)" v-if="exception.type == HoursOfOperationType.SelectHours"
                                            class="secondary-outlined td-btn icon-left mr-1">
                                        <icon name="faPlus" type="solid"></icon>
                                        Add Time
                                    </button>
                                    <button class="td-btn success icon-only" @click.prevent="finishEdit(index)"><icon name="faCheck" type="solid"></icon></button>
                                </td>

                            </tr>
                            <tr v-if="isEditing[index] && hasErrors(index)" class="edit-record table-row hours-row">
                                <td colspan="6">
                                    <error v-if="exceptionValidators[index].errors[`name-${index}`]" :error="exceptionValidators[index].errors[`name-${index}`]"></error>
                                    <error v-if="exceptionValidators[index].errors[`date-${index}`]" :error="exceptionValidators[index].errors[`date-${index}`]"></error>
                                    <error v-if="exceptionValidators[index].errors[`type-${index}`]" :error="exceptionValidators[index].errors[`type-${index}`]"></error>
                                    <error v-if="exceptionValidators[index].errors[`entries-${index}`]" :error="exceptionValidators[index].errors[`entries-${index}`]"></error>
                                </td>
                            </tr>
                            <!-- EDIT EXCEPTION TIME -->
                            <tr v-if="isEditing[index] && exception.entries.entries.length > 0 && exception.type == HoursOfOperationType.SelectHours"
                                class="hours-row edit-record table-row select-hours" v-for="(entry, entryIndex) in exception.entries.entries">
                                <td colspan="3">
                                    <div class="td-radio background ml-05">
                                        <label :for="'open-time' + index + entryIndex">
                                            <input type="radio"
                                                   v-model="exception.entries.entries[entryIndex].type"
                                                     :id="'open-time' + index + entryIndex"
                                                     :name="'open-time' + index + entryIndex"
                                                   :value="HoursOfOperationDisplayType.Open" 
                                                  />
                                            <span>Open </span>
                                        </label>

                                        <label :for="'after-time' + index + entryIndex">
                                            <input type="radio"
                                                   v-model="exception.entries.entries[entryIndex].type"
                                                   :id="'after-time' + index + entryIndex"
                                                   :name="'after-time' + index + entryIndex"
                                                   :value="HoursOfOperationDisplayType.AfterHours" 
                                                  />
                                            <span>After Hours </span>
                                        </label>
                                    </div>

                                </td>
                                <td colspan="2">
                                    <div class="hours-entry">
                                        <input type="time" v-model="exception.entries.entries[entryIndex].startTime"
                                               @change="editTime(index, entryIndex)"
                                               :class="[{ error: hasStartTimeError(index, entryIndex) }, 'td-input']" />
                                        -
                                        <input type="time" v-model="exception.entries.entries[entryIndex].endTime"
                                               :class="[{ error: hasEndTimeError(index, entryIndex) }, 'td-input']"
                                               @change="editTime(index, entryIndex)" />
                                        <button class="td-btn icon-only danger ml-1" @click="deleteEntry(index, entryIndex)">
                                            <icon type="solid" name="faTrashCan"></icon>
                                        </button>
                                    </div>
                                  
                                    <div class="errors">
                                        <error v-if="exceptionValidators[`${index}-${entryIndex}`]?.errors[`startTime-${index}-${entryIndex}`]"
                                               :error="exceptionValidators[`${index}-${entryIndex}`].errors[`startTime-${index}-${entryIndex}`]"></error>
                                        <error v-if="exceptionValidators[`${index}-${entryIndex}`]?.errors[`endTime-${index}-${entryIndex}`]"
                                               :error="exceptionValidators[`${index}-${entryIndex}`].errors[`endTime-${index}-${entryIndex}`]"></error>
                                        <error v-if="exceptionValidators[`${index}-${entryIndex}`]?.errors[`overlap-${index}-${entryIndex}`]"
                                               :error="exceptionValidators[`${index}-${entryIndex}`].errors[`overlap-${index}-${entryIndex}`]"></error>
                                        <error v-if="exceptionValidators[`${index}-${entryIndex}`]?.errors[`range-${index}-${entryIndex}`]"
                                               :error="exceptionValidators[`${index}-${entryIndex}`].errors[`range-${index}-${entryIndex}`]"></error>
                                    </div>
                                </td>
                            </tr>
                        </template>

                    </tbody>
                </table>
            </div>
            <teleport to="body">
                <modal @close="closeDeleteModal" style="max-width:800px;height:auto" v-if="showDeleteExceptionModal" class="delete-modal">
                    <template #header>
                        <h4 class="td-h4">Delete Hours Exception</h4>
                    </template>
                    <p class="my-1"> Are you sure you want to delete this hours exception?</p>
                    <template #footer>
                        <div class="actions">
                            <button class="td-btn btn primary-outlined" title="Cancel Delete Exception" @click="closeDeleteModal">Cancel</button>
                            <button class="danger td-btn" title="Delete Exception" @click="confirmDeleteException">Delete</button>
                        </div>
                    </template>
                </modal>
            </teleport>
        </div>
    </div>
</template>
<script>
    import script from './HoursExceptions';
    export default script;</script>

<style lang="less">
    @import "./HoursExceptions.less";
</style>